import { Show } from "@/shared/components/control";
/** Fundo do rodapé — Pexels #11668854, WebP otimizado (blur no CSS/scroll). */
import footerBackgroundImage from "@/assets/images/footer-pexels-11668854.webp";
import { site } from "@/config/site";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useFooterClock } from "../hooks";

const linkClass =
  "font-mono text-[0.62rem] uppercase tracking-[0.22em] text-ink-secondary transition-colors hover:text-ink sm:text-[0.68rem]";

/**
 * Rodapé cinematográfico: fundo com blur forte + parallax no scroll,
 * néon subtil nos cantos e revelação suave do conteúdo.
 */
export function SiteFooter() {
  const { t } = useTranslation();
  const { links, footer } = site;
  const showInstagram = Boolean(links.instagram?.trim());
  const time = useFooterClock(footer.clockTimeZone);
  const year = new Date().getFullYear();
  const locationBracket = `[ ${t("footer.clockLocation")} ${time} ]`;
  const rightsBracket = `[ ${year} © ${t("footer.rightsLine")} ]`;

  const footerRef = useRef<HTMLElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const neonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const footer = footerRef.current;
    const img = imgRef.current;
    const content = contentRef.current;
    const neon = neonRef.current;
    if (!footer || !img || !content) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      img.style.transform = "translate3d(0,0,0) scale(1.08)";
      img.style.filter = "blur(64px) brightness(0.34) saturate(0.82)";
      content.style.opacity = "1";
      content.style.transform = "none";
      if (neon) neon.style.opacity = "0.42";
      return;
    }

    let raf = 0;
    const tick = () => {
      const rect = footer.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const raw = (vh - rect.top) / (vh * 0.72);
      const enter = Math.min(1, Math.max(0, raw));
      const eased = 1 - (1 - enter) ** 1.45;

      const parallaxPx = (1 - eased) * 40;
      const scale = 1.08 + eased * 0.085;
      img.style.transform = `translate3d(0, ${parallaxPx}px, 0) scale(${scale})`;

      const blurPx = 56 + eased * 24;
      img.style.filter = `blur(${blurPx}px) brightness(${0.28 + eased * 0.12}) saturate(${0.75 + eased * 0.14}) contrast(${0.98 + eased * 0.04})`;

      content.style.opacity = String(0.74 + eased * 0.26);
      content.style.transform = `translate3d(0, ${(1 - eased) * 28}px, 0)`;

      if (neon) {
        neon.style.opacity = String(0.26 + eased * 0.58);
      }
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };

    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <footer
      ref={footerRef}
      className="mii-footer-cinematic relative z-10 min-h-svh w-full overflow-hidden shadow-[inset_0_0_140px_-50px_rgb(201_167_232/0.14),inset_0_-80px_100px_-60px_rgb(56_189_248/0.06)]"
    >
      <img
        ref={imgRef}
        src={footerBackgroundImage}
        alt=""
        width={1759}
        height={2200}
        className="absolute inset-0 z-0 min-h-full w-full min-w-full object-cover will-change-[transform,filter]"
        style={{
          transform: "translate3d(0, 40px, 0) scale(1.08)",
          filter: "blur(56px) brightness(0.28) saturate(0.75) contrast(0.98)",
        }}
        loading="eager"
        fetchPriority="low"
        decoding="async"
      />

      <div
        className="absolute inset-0 z-[1] bg-gradient-to-t from-canvas via-canvas/90 to-canvas/[0.52]"
        aria-hidden
      />

      <div
        ref={neonRef}
        className="absolute inset-0 z-[2] pointer-events-none opacity-[0.26] will-change-[opacity]"
        aria-hidden
      >
        <div className="absolute -left-[18%] -top-[12%] h-[min(58vw,520px)] w-[min(58vw,520px)] rounded-full bg-[radial-gradient(circle_at_38%_38%,rgb(201_167_232/0.32),rgb(167_139_250/0.08)_45%,transparent_68%)] blur-3xl mix-blend-screen" />
        <div className="absolute -right-[14%] top-[8%] h-[min(48vw,420px)] w-[min(48vw,420px)] rounded-full bg-[radial-gradient(circle_at_62%_42%,rgb(216_180_254/0.22),transparent_62%)] blur-3xl mix-blend-screen" />
        <div className="absolute -bottom-[10%] -left-[10%] h-[min(44vw,400px)] w-[min(44vw,400px)] rounded-full bg-[radial-gradient(circle_at_35%_72%,rgb(125_211_252/0.11),transparent_64%)] blur-3xl mix-blend-plus-lighter" />
        <div className="absolute -bottom-[8%] -right-[12%] h-[min(52vw,480px)] w-[min(52vw,480px)] rounded-full bg-[radial-gradient(circle_at_72%_68%,rgb(201_167_232/0.18),rgb(56_189_248/0.08)_50%,transparent_66%)] blur-3xl mix-blend-screen" />
      </div>

      <div
        ref={contentRef}
        className="relative z-10 flex min-h-svh flex-col justify-end px-4 pb-8 pt-32 will-change-[opacity,transform] sm:px-6 sm:pb-10 sm:pt-40 lg:px-10"
        style={{ opacity: 0.74, transform: "translate3d(0, 28px, 0)" }}
      >
        <div className="flex flex-1 flex-col justify-end">
          <div
            className="flex items-center justify-between gap-3 pb-6 md:hidden"
            aria-label={t("nav.ariaLabelFooter")}
          >
            <a
              href={links.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              {t("nav.twitter")}
            </a>
            <Show when={showInstagram}>
              <a
                href={links.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                {t("nav.instagram")}
              </a>
            </Show>
            <a
              href={links.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              {t("nav.telegram")}
            </a>
          </div>

          <p
            className="font-display text-[clamp(2.75rem,14vw,7.5rem)] font-extrabold leading-[0.88] tracking-tighter text-ink [text-shadow:0_2px_48px_rgb(0_0_0/0.5),0_0_80px_rgb(201_167_232/0.12)]"
            aria-label={site.brand}
          >
            <span className="uppercase">{site.brand}</span>
          </p>

          <nav
            className="mt-8 hidden items-center justify-between gap-4 border-t border-border-soft/40 pt-6 md:flex"
            aria-label={t("nav.ariaLabelFooter")}
          >
            <a
              href={links.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              {t("nav.twitter")}
            </a>
            <Show when={showInstagram}>
              <a
                href={links.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                {t("nav.instagram")}
              </a>
            </Show>
            <a
              href={links.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              {t("nav.telegram")}
            </a>
          </nav>

          <div className="mt-6 hidden items-center justify-between gap-4 border-t border-border-soft/30 pt-6 text-[0.68rem] leading-snug md:flex">
            <p className="font-mono uppercase tracking-[0.18em] text-ink-tertiary">
              {locationBracket}
            </p>
            <p className="max-w-md text-center font-normal tracking-tight text-ink-secondary">
              {t("footer.tagline")}
            </p>
            <p className="font-mono uppercase tracking-[0.14em] text-ink-tertiary">
              {rightsBracket}
            </p>
          </div>

          <p className="mt-6 text-center font-normal leading-relaxed tracking-tight text-ink-secondary md:hidden">
            {t("footer.tagline")}
          </p>
          <div className="mt-5 flex items-center justify-between gap-3 border-t border-border-soft/30 pt-5 text-[0.62rem] md:hidden">
            <p className="font-mono uppercase tracking-[0.16em] text-ink-tertiary">
              {locationBracket}
            </p>
            <p className="font-mono uppercase tracking-[0.12em] text-ink-tertiary">
              {rightsBracket}
            </p>
          </div>

          <div className="mx-auto mt-8 max-w-lg space-y-2 border-t border-border-soft/25 pt-6 text-center font-mono text-[0.58rem] uppercase leading-relaxed tracking-[0.14em] text-ink-muted sm:text-[0.6rem]">
            <p>{t("legal.ageDisclaimerLine1")}</p>
            <p>{t("legal.ageDisclaimerLine2")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
